import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container, CircularProgress, ThemeProvider, Typography,
} from '@material-ui/core';
import { useLazyQuery, useMutation } from '@apollo/client';
import { usePostHog } from 'posthog-js/react';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import {
  ContentContainer, HeaderBox, Title,
} from './ActivationSteps.styles';

import sleepioTheme from '@/themes/sleepioTheme';
import Button from '@/components/Button';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSleepioAuthorize } from '@/utils/sleepioActivationApi';
import { logError } from '@/utils/logError';
import { SLEEPIO_PDT_UID } from '@/utils/constants';
import { buildRedirectParam, isSafeRedirectUri } from '@/utils/urlHelpers';
import { ACTIVATE_PATIENT } from '@/apollo/mutations';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { linkPostHogIdentity, SLEEPIO_CANOPY_PROGRAM } from '@/utils/posthog';

type Status = 'loading' | 'activation_error' | 'auth_error' | 'fallback';

interface ErrorScreenProps {
  bodyKey: string;
  onRetry: () => void;
}

const ErrorScreen: React.FC<ErrorScreenProps> = ({ bodyKey, onRetry }) => {
  const { t } = useTranslation();
  return (
    <ContentContainer>
      <SleepioHeader />
      <Container maxWidth="xs" style={{ flex: 1, paddingTop: 32 }}>
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.complete.error_title')}
          </Title>
          <Typography color="textPrimary">
            {t(bodyKey)}
          </Typography>
        </HeaderBox>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          rounded
          data-testid="retry-button"
          onClick={onRetry}
        >
          {t('coverage.pre_onboarding.sleepio.activation.complete.retry')}
        </Button>
      </Container>
      <Footer />
    </ContentContainer>
  );
};

interface RouteParams {
  org_slug: string;
}

const ActivationCompleteInner: React.FC = () => {
  useAcceptedLanguage();

  const { t } = useTranslation();
  const { state } = useActivation();
  const { org_slug: paramOrgSlug } = useParams<RouteParams>();
  const { search } = useLocation();
  const history = useHistory();
  const base = useActivationBase();
  const [status, setStatus] = useState<Status>('loading');

  const posthog = usePostHog();
  const [activatePatient] = useMutation(ACTIVATE_PATIENT);
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);
  const activationStarted = useRef(false);

  // org_slug from URL params is authoritative; fall back to context if param is missing.
  const orgSlug = paramOrgSlug || state.orgSlug;

  // Schedules program content for a new user in Cortex.
  // Returns false and sets status if activation fails.
  const enrollNewUserInCortex = async (accessToken: string): Promise<boolean> => {
    try {
      const activateResponse = await activatePatient({
        variables: {
          pdtUid: SLEEPIO_PDT_UID,
          organizationSlug: orgSlug,
        },
        context: { headers: { Authorization: `Bearer ${accessToken}` } },
      });

      if (!activateResponse.data?.activatePatient?.success) {
        logError('[ActivationComplete]', 'activatePatient returned success=false');
        posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
        setStatus('activation_error');
        return false;
      }
    } catch (err) {
      logError('[ActivationComplete]', 'activatePatient failed', err);
      posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
      setStatus('activation_error');
      return false;
    }
    return true;
  };

  // Refreshes the session, obtains a short-lived exchange token, and redirects.
  // Used by both the create and login paths once Cortex setup is complete.
  const redirectViaExchangeToken = async (): Promise<void> => {
    const { data: refreshData, error: refreshError } = await getSupabaseClient().auth.refreshSession();

    if (refreshError || !refreshData?.session?.access_token || !refreshData?.session?.refresh_token) {
      logError('[ActivationComplete]', 'refreshSession failed', refreshError);
      posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
      // Refresh token was globally invalidated (e.g. user signed out on another device).
      // Clear the stale local session and redirect to fresh login instead of showing
      // an unrecoverable error screen.
      await getSupabaseClient().auth.signOut({ scope: 'local' });
      const redirectParam = buildRedirectParam(new URLSearchParams(search).get('redirectTo'));
      history.replace(`${base}/auth${redirectParam}`);
      return;
    }

    const { access_token: freshAccessToken, refresh_token: freshRefreshToken } = refreshData.session;

    let exchangeToken: string;
    try {
      ({ exchangeToken } = await activateSleepioAuthorize(freshAccessToken, freshRefreshToken));
    } catch (err) {
      logError('[ActivationComplete]', 'activateSleepioAuthorize failed', err);
      posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
      setStatus('auth_error');
      return;
    }

    // Fired once we have the exchange token — activation itself succeeded here,
    // regardless of whether there's a native app to hand off to.
    // send_instantly + sendBeacon so the event survives the navigation below.
    posthog?.capture('ActivationFlowCompleted', { program: SLEEPIO_CANOPY_PROGRAM }, {
      send_instantly: true,
      transport: 'sendBeacon',
    });

    const redirectTo = new URLSearchParams(search).get('redirectTo') ?? state.redirectTo;

    let nextUrl: string;
    if (redirectTo && isSafeRedirectUri(redirectTo)) {
      // Native: Canopy passes `redirectTo=sleep-companion://...` when opening CAP; it arrives
      // here as a URL param threaded through every navigation step.
      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      nextUrl = deepLinkUrl.toString();
    } else {
      // TODO (DAYL-1136): redirect to a CAP-internal download page (QR code + app store links)
      // modelled on DownloadSparkApp.tsx. For now, fall through to the fallback screen below
      // without firing an automatic redirect — the "Open Sleepio" button is the manual path.
      setHandoffUrl(null);
      setStatus('fallback');
      return;
    }

    // Persist the computed URL so the fallback button can reuse it if the
    // automatic redirect doesn't fire (e.g. desktop browser + native deep link).
    setHandoffUrl(nextUrl);
    // Show fallback so the user isn't left on a blank page if the redirect
    // does not fire automatically.
    setStatus('fallback');
    window.location.href = nextUrl;
  };

  const startActivation = async () => {
    setStatus('loading');

    try {
      const { data: { session } } = await getSupabaseClient().auth.getSession();

      if (!session?.access_token) {
        logError('[ActivationComplete]', 'no active session');
        posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
        setStatus('auth_error');
        return;
      }

      if (state.accountIntent === AccountIntent.Create) {
        const enrolled = await enrollNewUserInCortex(session.access_token);
        if (!enrolled) { return; }
      }

      // Await identity linking with a 2 s cap so the redirect is never blocked
      // indefinitely, but identify() fires before window.location.href is replaced.
      const email = session.user?.email;
      if (email) {
        await Promise.race([
          linkPostHogIdentity(getDistinctId, posthog, email, '[ActivationComplete]'),
          new Promise<void>((resolve) => { setTimeout(resolve, 2000); }),
        ]);
      }

      await redirectViaExchangeToken();
    } catch (err) {
      logError('[ActivationComplete]', 'unexpected error during activation', err);
      posthog?.capture('ActivationFlowErrored', { program: SLEEPIO_CANOPY_PROGRAM });
      setStatus('auth_error');
    }
  };

  useEffect(() => {
    // Guard against React 18 StrictMode's double-invoke in development.
    // Without this, activatePatient fires twice on mount, creating duplicate Cortex records.
    if (activationStarted.current) { return; }
    activationStarted.current = true;
    startActivation();
  }, []);

  if (status === 'loading') {
    return (
      <ContentContainer>
        <SleepioHeader />
        <Container
          maxWidth="xs"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress color="primary" style={{ marginBottom: 16 }} data-testid="loading-spinner" />
          <Typography color="textPrimary" align="center">
            {t(
              state.accountIntent === AccountIntent.Signin
                ? 'coverage.pre_onboarding.sleepio.activation.complete.loading_signin'
                : 'coverage.pre_onboarding.sleepio.activation.complete.loading',
            )}
          </Typography>
        </Container>
        <Footer />
      </ContentContainer>
    );
  }

  if (status === 'activation_error') {
    return (
      <ErrorScreen
        bodyKey="coverage.pre_onboarding.sleepio.activation.complete.error_activation"
        onRetry={startActivation}
      />
    );
  }

  if (status === 'auth_error') {
    return (
      <ErrorScreen
        bodyKey="coverage.pre_onboarding.sleepio.activation.complete.error_authorize"
        onRetry={startActivation}
      />
    );
  }

  // Fallback: shown after firing the native deep-link redirect (or when no redirect applies).
  // On native, the "Open Sleepio" button retries the handoff if the app didn't open automatically.
  // On web/desktop (handoffUrl is null), the button is a no-op until DAYL-1136 adds a download page.
  return (
    <ContentContainer>
      <SleepioHeader />
      <Container maxWidth="xs" style={{ flex: 1, paddingTop: 32 }}>
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.complete.fallback_title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.complete.fallback_body')}
          </Typography>
        </HeaderBox>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          rounded
          data-testid="open-sleepio-button"
          onClick={() => { if (handoffUrl) { window.location.href = handoffUrl; } }}
        >
          {t('coverage.pre_onboarding.sleepio.activation.complete.fallback_open')}
        </Button>
      </Container>
      <Footer />
    </ContentContainer>
  );
};

export const ActivationComplete: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <ActivationCompleteInner />
  </ThemeProvider>
);
